import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Star } from "lucide-react";
import { CONTACT, IMAGES } from "../../data/site";
import { useEstimate } from "./EstimateDialog";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

const HEAD = ["Charlotte's Trusted", "Residential & Commercial", "Property Solutions Company"];

export const Hero = () => {
  const { open } = useEstimate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative min-h-[100svh] overflow-hidden bg-ffc-ink grain" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroHome} alt="Residential & commercial property solutions in Charlotte, NC — modern renovated home exterior" className="h-full w-full object-cover" />
        {/* Layered scrims for guaranteed white-text legibility while keeping architecture visible */}
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/30" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-center px-5 pt-28 pb-16 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 backdrop-blur-md">
          <span className="flex gap-0.5">
            {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-[#FFC107] text-[#FFC107]" />)}
          </span>
          <span className="text-xs font-semibold text-white/90">Trusted by homeowners & businesses across Charlotte</span>
        </motion.div>

        <h1 className="font-display max-w-6xl text-[11vw] font-extrabold leading-[0.98] text-white sm:text-6xl lg:text-[5rem]">
          {HEAD.map((l, i) => (
            <span key={i} className="mask-line">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {i === 1 ? <span className="text-ffc-red">{l}</span> : l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-7 max-w-xl text-base text-white/80 sm:text-lg">
          Professional Residential & Commercial Repairs, Installations, Renovations and Maintenance.
          We deliver dependable craftsmanship, transparent pricing, and exceptional service throughout
          Charlotte and surrounding areas.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4">
          <button onClick={open} data-testid="hero-estimate-btn"
            className="btn-red group inline-flex items-center gap-2 rounded-full px-7 py-4 text-base font-semibold hover:bg-ffc-red">
            Get Free Estimate
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>
          <a href={CONTACT.phoneHref} data-testid="hero-call-btn"
            className="btn-outline-dark inline-flex items-center gap-2 rounded-full px-7 py-4 text-base font-semibold">
            <Phone className="h-5 w-5" /> Call Now
          </a>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-12 flex flex-wrap gap-x-10 gap-y-4 border-t border-white/15 pt-7">
          {[["10+", "Years of Craft"], ["2,500+", "Projects Completed"], ["5.0★", "Average Rating"]].map(([n, l]) => (
            <div key={l}>
              <div className="font-display text-3xl font-extrabold text-white">
                {n.endsWith("★") ? (
                  <>
                    {n.slice(0, -1)}
                    <span className="text-[#FFC107]">★</span>
                  </>
                ) : n}
              </div>
              <div className="text-xs font-medium uppercase tracking-wider text-white/60">{l}</div>
            </div>
          ))}
          <div className="flex items-center gap-2 text-white/80">
            <ShieldCheck className="h-5 w-5 text-ffc-red" />
            <span className="text-sm font-semibold">Licensed & Insured</span>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};
